import { useState, useEffect } from 'react';
import { money, fullName, apellidoNombre, fmtDateTime, linkValue } from '../util';
import { useAuth } from '../../hooks/useAuth';
import TicketQR from '../../components/TicketQR';
import BotonCompartirLink from '../../components/BotonCompartirLink';

export default function TabVentas({ event, api, reload }) {
  const [tab, setTab] = useState('links');  // la primera del menú
  const [links, setLinks] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [mesas, setMesas] = useState([]);
  const [lots, setLots] = useState([]);
  const [orgLogo, setOrgLogo] = useState(null); // logo del boliche (Mi Web) para el QR

  const load = () => {
    api.get(`/api/rrpp-links?event_id=${event.id}`).then(setLinks).catch(() => {});
    api.get(`/api/tickets?event_id=${event.id}`).then(setTickets).catch(() => {});
    api.get(`/api/mesas?event_id=${event.id}`).then(setMesas).catch(() => {});
    api.get(`/api/events/${event.id}/lots`).then(setLots).catch(() => {});
  };
  useEffect(() => {
    load();
    api.get('/api/org').then(o => setOrgLogo(o?.logo_url || null)).catch(() => {});
  }, []); // eslint-disable-line

  const pct = event.commission_pct == null ? 10 : Number(event.commission_pct);
  // Cada canal se cuenta por separado (no derivamos uno restando del total, así boletería
  // no se cuela en online). RRPP = generado (max_uses); online y boletería = tickets pagados.
  // Las mesas no son un canal aparte: la mesa vendida de forma tradicional (la genera dueño/RRPP)
  // suma en RRPP; la vendida online suma en Online. No hay tarjeta "Mesas".
  const soldMesas = mesas.filter(m => m.status === 'sold');
  const mesasTrad = soldMesas.filter(m => m.sold_channel !== 'online');   // vendidas tradicional (dueño/RRPP)
  const mesasOnline = soldMesas.filter(m => m.sold_channel === 'online'); // vendidas por la web
  const mesaTrad = mesasTrad.reduce((a, m) => a + Number(m.price || 0), 0);
  const mesaOnline = mesasOnline.reduce((a, m) => a + Number(m.price || 0), 0);
  const ventaRRPP = links.reduce((a, l) => a + linkValue(l), 0) + mesaTrad;
  const ventaOnline = (event.metrics?.recaudado_online || 0) + mesaOnline; // se cuenta al pagar (backend) + mesas online
  const ventaBoleteria = event.metrics?.recaudado_boleteria || 0; // ventas de la caja de boletería
  const ventaTotal = ventaRRPP + ventaOnline + ventaBoleteria;
  const onlinePct = ventaTotal ? Math.round(ventaOnline / ventaTotal * 100) : 0;

  // VENDIDO = capacidad de los links RRPP (max_uses) + entradas online + boletería + PERSONAS
  // de las mesas vendidas (cada mesa vendida cuenta por su capacidad, se hayan generado o no
  // los QR individuales). Se reparten por canal para que cada tarjeta cuadre con su monto.
  const mesaPersTrad = mesasTrad.reduce((a, m) => a + Number(m.capacity || 0), 0);
  const mesaPersOnline = mesasOnline.reduce((a, m) => a + Number(m.capacity || 0), 0);
  // EL SOLO TRASLADO NO ES UNA PERSONA. Es el micro sin entrada: su QR no da acceso y el
  // que lo compra puede no entrar nunca. Suma plata (va en los montos de arriba) pero no
  // en el conteo de vendidos, que es cuánta gente viene.
  const esTraslado = (x) => !!x.solo_traslado;
  const onlineSold = (event.metrics?.online_sold ?? tickets.filter(t => t.channel === 'online' && !esTraslado(t) && (t.status === 'pagado' || t.status === 'usado')).length) + mesaPersOnline;
  const boleteriaSold = event.metrics?.boleteria_sold || 0;
  const invitaciones = event.metrics?.invitaciones || 0;
  const rrppCapacity = links.reduce((a, l) => a + (esTraslado(l) ? 0 : (l.max_uses || 0)), 0);
  const rrppVendidas = rrppCapacity + mesaPersTrad; // "a la venta" por RRPP/tradicional (links + mesas)
  const vendidas = rrppVendidas + onlineSold + boleteriaSold;
  // GENERADOS = QRs efectivamente creados
  const generadas = tickets.filter(t => t.status !== 'cancelado').length;
  const rrppGen = tickets.filter(t => t.link_id && t.status !== 'cancelado').length;

  // Vendedor estrella: el RRPP con más QR vendidos (monto sin descontar comisión).
  // Solo cuentan los de rol RRPP: el dueño y los socios también generan links (sobre todo
  // invitaciones) y, sumados, salían ellos como vendedor estrella de su propia noche.
  const byRrpp = {};
  links.forEach(l => {
    if (!l.rrpp_id || l.rrpp_role !== 'rrpp') return;
    byRrpp[l.rrpp_id] = byRrpp[l.rrpp_id] || { name: l.rrpp_name || '—', qrs: 0, monto: 0 };
    byRrpp[l.rrpp_id].qrs += (l.max_uses || 0);
    byRrpp[l.rrpp_id].monto += linkValue(l);
  });
  const star = Object.values(byRrpp).filter(r => r.qrs > 0).sort((a, b) => b.qrs - a.qrs || b.monto - a.monto)[0] || null;

  // La pestaña del micro aparece solo si el evento tiene traslado: en una noche sin
  // micro sería una lista siempre vacía. Se mira por lote (lo que se configuró) y no por
  // los QR ya emitidos, así está disponible desde antes de que alguien genere el suyo.
  const hayTraslado = lots.some(l => l.has_transport) || tickets.some(t => t.transport_qr);

  return (
    <div className="lb-fade" style={{ paddingTop: 30 }}>
      <div className="tk-cards" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 18 }}>
        <Card label="Venta total" value={money(ventaTotal)} sub={`${vendidas} vendidos${invitaciones ? ` + ${invitaciones} invitados` : ''} · ${generadas} generados`} accent />
        <Card label="Online" value={money(ventaOnline)} sub={`${onlineSold} vendidos · ${onlinePct}%`} />
        <Card label="RRPP" value={money(ventaRRPP)} sub={`${rrppVendidas} a la venta · ${rrppGen} redimidos`} />
        {boleteriaSold > 0 && <Card label="Boletería" value={money(ventaBoleteria)} sub={`${boleteriaSold} vendidos`} />}
        {/* Invitaciones ACEPTADAS. Aparte de la venta a proposito: no entraron plata, y
            sumarlas a "vendidos" hundiria el ticket promedio. El total de gente que
            viene es vendidos + invitaciones, y se muestra en la primera tarjeta. */}
        {invitaciones > 0 && <Card label="Invitaciones" value={invitaciones} sub="aceptadas · sin cargo" />}
      </div>

      {star && (
        <div className="lb-card" style={{ marginTop: 22, padding: '20px 24px', display: 'flex', alignItems: 'center', gap: 18, flexWrap: 'wrap', border: '1px solid rgba(212,175,55,0.5)', background: 'linear-gradient(180deg, rgba(212,175,55,0.10), rgba(212,175,55,0.02))' }}>
          <div style={{ fontSize: 34, lineHeight: 1 }}>⭐</div>
          <div style={{ flex: 1, minWidth: 180 }}>
            <div className="lb-eyebrow" style={{ color: '#d4af37' }}>Vendedor estrella de la noche</div>
            <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 24, textTransform: 'uppercase', color: '#fff', marginTop: 4 }}>{star.name}</div>
          </div>
          <div style={{ display: 'flex', gap: 28, flexWrap: 'wrap' }}>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: '#fff' }}>{star.qrs}</div>
              <div className="lb-eyebrow">QR vendidos</div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 22, color: '#fff' }}>{money(star.monto)}</div>
              <div className="lb-eyebrow">Monto total</div>
            </div>
          </div>
        </div>
      )}

      <div className="lb-subtabs" style={{ marginTop: 28 }}>
        <button className={tab === 'links' ? 'active' : ''} onClick={() => setTab('links')}>Links por RRPP</button>
        <button className={tab === 'online' ? 'active' : ''} onClick={() => setTab('online')}>Links online</button>
        <button className={tab === 'lote' ? 'active' : ''} onClick={() => setTab('lote')}>Venta por lote</button>
        <button className={tab === 'rrpp' ? 'active' : ''} onClick={() => setTab('rrpp')}>Venta por RRPP</button>
        <button className={tab === 'asistentes' ? 'active' : ''} onClick={() => setTab('asistentes')}>Lista de asistentes</button>
        {hayTraslado && <button className={tab === 'traslados' ? 'active' : ''} onClick={() => setTab('traslados')}>Lista de traslados</button>}
      </div>

      {tab === 'lote' && <PorLote lots={lots} tickets={tickets} mesas={soldMesas} links={links} />}
      {tab === 'rrpp' && <PorRRPP links={links} mesas={soldMesas} pct={pct} />}
      {tab === 'links' && <LinksGenerados mode="rrpp" event={event} links={links} tickets={tickets} mesas={soldMesas} api={api} reload={load} orgLogo={orgLogo} />}
      {tab === 'asistentes' && <Asistentes event={event} api={api} />}
      {tab === 'traslados' && <Asistentes event={event} api={api} modo="traslado" />}
      {tab === 'online' && <LinksGenerados mode="online" event={event} links={links} tickets={tickets} mesas={soldMesas} api={api} reload={load} orgLogo={orgLogo} />}
    </div>
  );
}

// Lista de asistentes: todos los que YA generaron su QR, con sus datos y el botón para
// darles el ingreso a mano (sin escáner). El historial de asistencias lo calcula el
// backend: la pantalla solo tiene los tickets de esta noche.
//
// Va paginada y ordenada por APELLIDO: en una noche de 1000 personas, una lista corrida
// es imposible de recorrer, y a alguien se lo busca por apellido. El backend ya devuelve
// ordenado por (apellido, nombre).
const POR_PAGINA = 100;

// Barra de paginado simple, para las listas donde no hace falta el salto por apellido
// (los links de RRPP y la venta online). Aparece solo si hay más de una página.
function BarraPaginado({ pagActual, paginas, desde, total, onIr }) {
  if (paginas <= 1) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
      <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={pagActual <= 1} onClick={() => onIr(Math.max(1, pagActual - 1))}>← Anterior</button>
      <span style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>
        <b style={{ color: '#fff' }}>{desde + 1}–{Math.min(desde + POR_PAGINA, total)}</b> de {total}
        <span style={{ color: 'var(--ink-600)' }}> · página {pagActual} de {paginas}</span>
      </span>
      <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={pagActual >= paginas} onClick={() => onIr(Math.min(paginas, pagActual + 1))}>Siguiente →</button>
      <select className="lb-select" style={{ padding: '6px 26px 6px 10px', fontSize: 12 }} value={pagActual} onChange={e => onIr(Number(e.target.value))}>
        {Array.from({ length: paginas }, (_, i) => <option key={i + 1} value={i + 1}>Página {i + 1}</option>)}
      </select>
    </div>
  );
}

function Asistentes({ event, api, modo = 'puerta' }) {
  // La lista de TRASLADOS es la misma pantalla con otro estado: el control del micro es
  // aparte del de la puerta (una persona puede haber subido y todavía no haber entrado),
  // así que cambia qué columna se mira y qué se marca a mano. Duplicar el componente
  // habría dejado dos listas que se desincronizan a la primera corrección.
  const esTraslado = modo === 'traslado';
  const [rows, setRows] = useState(null);   // null = cargando
  const [q, setQ] = useState('');
  const [validando, setValidando] = useState(null);
  const [pag, setPag] = useState(1);
  const [ficha, setFicha] = useState(null);   // asistente cuya ficha se está viendo

  const load = () => api.get(`/api/tickets/attendees?event_id=${event.id}${esTraslado ? '&solo=traslado' : ''}`)
    .then(r => setRows(Array.isArray(r) ? r : []))
    .catch(() => setRows([]));
  useEffect(() => { load(); }, [event.id]); // eslint-disable-line

  async function validar(t) {
    const nombre = fullName(t.buyer_name, t.buyer_lastname) || 'esta persona';
    // Marcar el ingreso a mano quema el QR: si es un error, la persona llega a la
    // puerta y su entrada ya figura usada. Por eso se confirma con nombre y lote.
    const pregunta = esTraslado
      ? `¿Marcar que ${nombre} usó el traslado?\n\n${t.lot_name || 'Sin lote'} · ${t.transport_qr}\n\nSu QR de traslado queda usado y no se puede volver a escanear.`
      : `¿Dar el ingreso a ${nombre}?\n\n${t.lot_name || 'Sin lote'} · ${t.qr_code}\n\nSu QR queda usado y no se puede volver a escanear.`;
    if (!window.confirm(pregunta)) return;
    setValidando(t.id);
    try { await api.patch(`/api/tickets/${t.id}/${esTraslado ? 'scan-traslado' : 'scan'}`, {}); await load(); }
    catch (e) { alert(e.message); }
    finally { setValidando(null); }
  }

  if (rows === null) return <div style={{ padding: '40px 0', textAlign: 'center', color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>;

  const term = q.trim().toLowerCase();
  const filtradas = !term ? rows : rows.filter(t =>
    // Se busca sobre "APELLIDO, Nombre" y también sobre el nombre suelto: quien escribe
    // "juan perez" tiene que encontrarlo aunque en la lista figure "PEREZ, Juan".
    (apellidoNombre(t.buyer_name, t.buyer_lastname) || '').toLowerCase().includes(term) ||
    (fullName(t.buyer_name, t.buyer_lastname) || '').toLowerCase().includes(term) ||
    (t.buyer_dni || '').includes(term) ||
    (t.qr_code || '').toLowerCase().includes(term) ||
    (t.transport_qr || '').toLowerCase().includes(term));
  const ingresaron = rows.filter(t => (esTraslado ? !!t.transport_used_at : t.status === 'usado')).length;
  const COLS = '1.5fr 0.5fr 1fr 1.1fr 0.7fr auto';

  // Paginado de a 100: una noche de 1000 personas en una sola lista no se puede recorrer.
  const paginas = Math.max(1, Math.ceil(filtradas.length / POR_PAGINA));
  const pagActual = Math.min(pag, paginas);          // si el filtro achica la lista
  const desde = (pagActual - 1) * POR_PAGINA;
  const visibles = filtradas.slice(desde, desde + POR_PAGINA);

  return (
    <div className="lb-fade" style={{ marginTop: 22 }}>
      <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap', marginBottom: 12 }}>
        <input className="lb-input" style={{ maxWidth: 280 }} value={q} onChange={e => { setQ(e.target.value); setPag(1); }} placeholder="Buscar por apellido, nombre, DNI o QR" />
        <div style={{ fontSize: 12, color: 'var(--ink-700)' }}>
          <b style={{ color: '#fff' }}>{rows.length}</b> {esTraslado ? 'con traslado' : 'con QR generado'} · <b style={{ color: '#fff' }}>{ingresaron}</b> {esTraslado ? 'ya subieron' : 'ya ingresaron'}
        </div>
        <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ marginLeft: 'auto' }} onClick={load}>Actualizar</button>
      </div>

      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: COLS }}>
          <span>Apellido y nombre</span><span>Edad</span><span>Localidad</span><span>Lote</span><span>Vino antes</span><span style={{ textAlign: 'right' }}>{esTraslado ? 'Traslado' : 'Ingreso'}</span>
        </div>
        {visibles.map(t => {
          const usado = esTraslado ? !!t.transport_used_at : t.status === 'usado';
          return (
            <div key={t.id} className="lb-row" style={{ gridTemplateColumns: COLS, alignItems: 'center' }}>
              <div>
                <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{apellidoNombre(t.buyer_name, t.buyer_lastname) || '—'}</div>
                <div style={{ fontSize: 10, color: 'var(--ink-600)', fontFamily: 'monospace', letterSpacing: '0.06em' }}>{esTraslado ? t.transport_qr : t.qr_code}</div>
              </div>
              <span style={{ color: 'var(--ink-800)' }}>{t.buyer_age || '—'}</span>
              <span style={{ color: 'var(--ink-800)', fontSize: 13 }}>{t.buyer_city || '—'}</span>
              <span style={{ color: 'var(--ink-800)', fontSize: 12.5, textTransform: 'uppercase' }}>{t.lot_name || '—'}</span>
              {/* "Vino antes" = eventos ANTERIORES en los que ingresó, no compras. */}
              <span style={{ color: t.eventos_previos > 0 ? '#fff' : 'var(--ink-600)', fontWeight: t.eventos_previos > 0 ? 600 : 400 }}>
                {t.eventos_previos === 0 ? '1ª vez' : `${t.eventos_previos} ${t.eventos_previos === 1 ? 'vez' : 'veces'}`}
              </span>
              <span style={{ textAlign: 'right', display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 5 }}>
                {usado
                  ? <span className="lb-status lb-status--paid" title={fmtDateTime(esTraslado ? t.transport_used_at : t.scanned_at)}>{esTraslado ? 'Subió' : 'Ingresó'}</span>
                  : <button className="lb-btn lb-btn--white lb-btn--sm" disabled={validando === t.id} onClick={() => validar(t)}>
                      {validando === t.id ? '…' : (esTraslado ? 'Marcar subió' : 'Dar ingreso')}
                    </button>}
                {/* En la lista del micro también se ve si además entró al boliche: son dos
                    controles distintos y la diferencia entre los dos es un dato. */}
                {esTraslado && (
                  <span style={{ fontSize: 10, color: t.status === 'usado' ? 'var(--ink-600)' : 'var(--red-400)' }}>
                    {t.solo_traslado ? 'solo traslado · no es entrada'
                      : t.status === 'usado' ? 'entró al boliche' : 'todavía no entró'}
                  </span>
                )}
                <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ whiteSpace: 'nowrap', fontSize: 11 }} onClick={() => setFicha(t)}>Ver detalles</button>
              </span>
            </div>
          );
        })}
        {visibles.length === 0 && (
          <div style={{ padding: '20px 0', fontSize: 13, color: 'var(--ink-600)' }}>
            {rows.length === 0
              ? (esTraslado ? 'Ningún lote de este evento tiene traslado, o todavía nadie generó su QR.' : 'Todavía nadie generó su QR.')
              : 'Nadie coincide con la búsqueda.'}
          </div>
        )}
      </div>

      {/* Paginador. Aparece solo si hay más de una página: con 40 asistentes, unos
          controles de página son ruido. */}
      {paginas > 1 && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 12, flexWrap: 'wrap' }}>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={pagActual <= 1} onClick={() => setPag(p => Math.max(1, p - 1))}>← Anterior</button>
          <span style={{ fontSize: 12.5, color: 'var(--ink-700)' }}>
            <b style={{ color: '#fff' }}>{desde + 1}–{Math.min(desde + POR_PAGINA, filtradas.length)}</b> de {filtradas.length}
            <span style={{ color: 'var(--ink-600)' }}> · página {pagActual} de {paginas}</span>
          </span>
          <button className="lb-btn lb-btn--ghost lb-btn--sm" disabled={pagActual >= paginas} onClick={() => setPag(p => Math.min(paginas, p + 1))}>Siguiente →</button>
          {/* Salto directo: con 10 páginas, llegar a la M de "Martinez" a botonazos es
              peor que la lista larga que estamos arreglando. */}
          <select className="lb-select" style={{ padding: '6px 26px 6px 10px', fontSize: 12 }} value={pagActual} onChange={e => setPag(Number(e.target.value))}>
            {Array.from({ length: paginas }, (_, i) => {
              const ini = filtradas[i * POR_PAGINA];
              const letra = (apellidoNombre(ini?.buyer_name, ini?.buyer_lastname) || '?').slice(0, 12);
              return <option key={i + 1} value={i + 1}>{i + 1} · desde {letra}</option>;
            })}
          </select>
        </div>
      )}

      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>
        Ordenada por apellido. "Vino antes" cuenta los eventos anteriores del boliche en los que la persona ingresó (se identifica por email o DNI).
        {esTraslado && ' El QR del traslado es distinto al de la puerta y se controla aparte: usar el traslado no marca el ingreso al boliche.'}
      </div>

      {ficha && <FichaAsistente t={ficha} api={api} onClose={() => setFicha(null)} />}
    </div>
  );
}

// FICHA DE UN ASISTENTE: la línea de tiempo de su QR.
//
// Nace de un caso real: una persona llegó a la puerta y su entrada figuraba ya usada.
// Con las horas exactas de cada paso se puede cruzar con las cámaras y ver qué pasó.
function FichaAsistente({ t, api, onClose }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');

  useEffect(() => {
    api.get(`/api/tickets/${t.id}/detalle`)
      .then(setD)
      .catch(e => setErr(e.message || 'No se pudo cargar'));
  }, [t.id]); // eslint-disable-line

  const canal = (c) => ({ rrpp: 'RRPP', online: 'Venta online', boleteria: 'Boletería', invitacion: 'Invitación', manual: 'Carga manual' }[c] || c || '—');
  const motivo = (r) => ({
    already_used: 'Ya figuraba ingresada',
    already_used_traslado: 'El QR de traslado ya estaba usado',
    cancelled: 'La entrada estaba cancelada',
    not_found: 'QR no reconocido',
  }[r] || r);

  return (
    <div onClick={onClose}
      style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.72)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 60, padding: 16 }}>
      <div onClick={e => e.stopPropagation()} className="lb-card"
        style={{ maxWidth: 460, width: '100%', padding: '22px 24px', maxHeight: '86vh', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontSize: 16 }}>{apellidoNombre(t.buyer_name, t.buyer_lastname) || '—'}</div>
            <div style={{ fontSize: 11, color: 'var(--ink-600)', fontFamily: 'monospace', letterSpacing: '0.06em', marginTop: 2 }}>{t.qr_code}</div>
          </div>
          <button className="lb-iconbtn" onClick={onClose}>✕</button>
        </div>

        {err && <div className="lb-err" style={{ marginTop: 14 }}>{err}</div>}
        {!d && !err && <div style={{ marginTop: 18, color: 'var(--ink-600)', fontSize: 13 }}>Cargando…</div>}

        {d && (
          <>
            <div style={{ marginTop: 18 }}>
              <Dato k="Origen" v={canal(d.channel)} />
              {d.rrpp_name && <Dato k="RRPP" v={d.rrpp_name} />}
              <Dato k="Lote" v={d.lot_name || '—'} />
              {d.link_code && <Dato k="Link" v={`/${d.link_code}${d.max_uses ? ` · ${d.max_uses} ${d.max_uses === 1 ? 'entrada' : 'entradas'}` : ''}`} />}
              {d.link_note && <Dato k="Nota del link" v={d.link_note} />}
            </div>

            <div className="lb-eyebrow" style={{ margin: '20px 0 8px' }}>Línea de tiempo</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
              {d.link_created_at && <Paso hora={d.link_created_at} txt="El RRPP generó el link" />}
              <Paso hora={d.created_at} txt={d.channel === 'boleteria' ? 'Se vendió en boletería' : 'El comprador generó su QR'} />
              {/* Cada movimiento dice QUIÉN lo hizo: distingue "lo escaneó Pedro en la
                  puerta" de "alguien lo validó de la lista sin querer". */}
              {(d.movimientos || []).map((m, i) => (
                <Paso key={i} hora={m.at}
                  txt={m.reason === 'ok' ? 'Ingresó por la puerta'
                    : m.reason === 'manual' ? 'Ingreso dado a mano desde el panel'
                    : `Rebotó: ${motivo(m.reason)}`}
                  quien={quienFue(m)}
                  ok={m.reason === 'ok'} aviso={m.reason === 'manual'} mal={m.reason !== 'ok' && m.reason !== 'manual'} />
              ))}
              {/* Entradas viejas: el registro de movimientos empezó el 18/8, así que para
                  ellas solo está la marca de ingreso del propio ticket. */}
              {!(d.movimientos || []).length && (d.scanned_at
                ? <Paso hora={d.scanned_at} txt={d.auto_ingreso ? 'Ingreso automático (boletería en la puerta)' : 'Ingresó por la puerta'} ok />
                : <Paso txt="Todavía no ingresó" />)}
              {(d.movimientos || []).length > 0 && !d.scanned_at && <Paso txt="Todavía no ingresó" />}
              {/* El micro es un control aparte, con su propio QR y su propio estado. Va
                  al final de la línea de tiempo pero se aclara que es otra cosa: alguien
                  puede haber subido al micro y no haber entrado nunca al boliche. */}
              {d.transport_qr && (d.transport_used_at
                ? <Paso hora={d.transport_used_at} txt="Usó el traslado" ok />
                : <Paso txt="Tiene traslado, pero no figura que lo haya usado" />)}
            </div>

            {(d.movimientos || []).length === 0 && (
              <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginTop: 12, lineHeight: 1.5 }}>
                De esta entrada no hay registro de quién la escaneó: se guarda desde el 20/8, y las noches anteriores no quedaron registradas.
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

function Dato({ k, v }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, padding: '5px 0', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
      <span style={{ fontSize: 12.5, color: 'var(--ink-600)' }}>{k}</span>
      <span style={{ fontSize: 13, color: '#fff', textAlign: 'right' }}>{v}</span>
    </div>
  );
}

// Quién hizo el movimiento: el puesto de escaneo, o el usuario del panel si fue a mano.
// El id del dispositivo va detrás para distinguir dos teléfonos con el mismo link.
function quienFue(m) {
  if (m.actor) return `${m.actor} · desde el panel`;
  const puesto = m.puesto || (m.puerta === 'traslado' ? 'escáner de traslado' : 'escáner sin nombre');
  return m.device_id ? `${puesto} · equipo ${m.device_id}` : puesto;
}

function Paso({ hora, txt, quien, ok, mal, aviso }) {
  const color = mal ? 'var(--red-400)' : aviso ? '#ffb300' : ok ? '#2ecc71' : 'var(--ink-600)';
  return (
    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', padding: '7px 0' }}>
      <span style={{ color, fontSize: 15, lineHeight: 1 }}>{mal ? '✕' : aviso ? '!' : ok ? '✓' : '·'}</span>
      <div style={{ flex: 1 }}>
        <div style={{ fontSize: 13, color: mal ? 'var(--red-400)' : aviso ? '#ffb300' : '#fff' }}>{txt}</div>
        {quien && <div style={{ fontSize: 12, color: 'var(--ink-800)' }}>{quien}</div>}
        {hora && <div style={{ fontSize: 11.5, color: 'var(--ink-600)' }}>{fmtDateTime(hora)}</div>}
      </div>
    </div>
  );
}

function Card({ label, value, sub, accent }) {
  return (
    <div className="lb-card" style={{ padding: '24px 26px' }}>
      <div className="lb-eyebrow">{label}</div>
      <div style={{ fontFamily: 'var(--font-display)', fontWeight: 700, fontSize: 28, color: accent ? 'var(--red-400)' : '#fff', marginTop: 6 }}>{value}</div>
      <div style={{ fontSize: 12, color: 'var(--ink-700)', marginTop: 4 }}>{sub}</div>
    </div>
  );
}

// Resumen por lote: cada lote con la cantidad VENDIDA por los vendedores y su monto. "Vendida" =
// links generados (max_uses, RRPP + tradicional) + entradas online/boletería/manuales pagadas.
// NO cuenta los QR que el comprador redimió (channel 'rrpp'): mucha gente genera su QR a último
// momento y el dato que importa es la venta (para organizar traslados y ver cupos).
//
// El desglose por CIUDAD se quitó: la ciudad la escribe el comprador a mano, así que
// "Funes", "funes" y "roldan santa fe" aparecían como tres ciudades distintas y el
// detalle confundía más de lo que ayudaba. El 🚌 sigue marcando el lote con traslado.
function PorLote({ lots, tickets, mesas, links }) {
  const paid = t => t.status === 'pagado' || t.status === 'usado';
  const rows = (lots || []).map(l => {
    const genLinks = (links || []).filter(x => x.lot_id === l.id).reduce((a, x) => a + (x.max_uses || 0), 0);
    const nonRrpp = (tickets || []).filter(t => t.lot_id === l.id && paid(t) && t.channel !== 'rrpp' && t.channel !== 'mesa');
    const qty = genLinks + nonRrpp.length;
    return { lot: l, qty, monto: qty * Number(l.price || 0) };
  }).filter(r => r.qty > 0).sort((a, b) => b.monto - a.monto);
  const totalQ = rows.reduce((a, r) => a + r.qty, 0);
  const totalM = rows.reduce((a, r) => a + r.monto, 0);

  return (
    <div className="lb-fade" style={{ marginTop: 22 }}>
      <div style={{ fontSize: 11.5, color: 'var(--ink-600)', marginBottom: 10, lineHeight: 1.5 }}>
        <b style={{ color: 'var(--ink-800)' }}>Vendidas</b> = lo que vendieron los vendedores (links RRPP + tradicional generados + online + boletería), no lo que el comprador ya redimió. Así ves la venta real aunque la gente genere su QR a último momento.
      </div>
      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.8fr 0.8fr 1fr' }}>
          <span>Lote</span><span>Vendidas</span><span>Monto</span>
        </div>
        {rows.map(r => {
          return (
            <div key={r.lot.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.06)' }}>
              <div className="lb-row" style={{ gridTemplateColumns: '1.8fr 0.8fr 1fr', borderBottom: 0, alignItems: 'center' }}>
                <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 13, textTransform: 'uppercase', color: '#fff' }}>{r.lot.name}{r.lot.has_transport ? ' 🚌' : ''}</span>
                <span style={{ color: 'var(--ink-800)' }}>{r.qty}</span>
                <span style={{ color: '#fff', fontWeight: 600 }}>{money(r.monto)}</span>
              </div>
            </div>
          );
        })}
        {rows.length === 0 && <div style={{ padding: '20px 0', fontSize: 13, color: 'var(--ink-600)' }}>Sin entradas vendidas todavía.</div>}
        {rows.length > 0 && (
          <div className="lb-row" style={{ gridTemplateColumns: '1.8fr 0.8fr 1fr', borderTop: '1px solid rgba(255,255,255,0.12)' }}>
            <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>Total</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{totalQ}</span>
            <span style={{ color: '#fff', fontWeight: 700 }}>{money(totalM)}</span>
          </div>
        )}
      </div>

      {mesas && mesas.length > 0 && (
        <>
          <div className="lb-eyebrow" style={{ margin: '24px 0 10px' }}>Mesas vendidas ({mesas.length})</div>
          <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '2fr 0.8fr 1fr' }}>
              <span>Mesa</span><span>Personas</span><span>Monto</span>
            </div>
            {mesas.map(m => (
              <div key={m.id} className="lb-row" style={{ gridTemplateColumns: '2fr 0.8fr 1fr' }}>
                <span style={{ color: '#fff', fontWeight: 600, fontSize: 13 }}>{m.sector_name ? `${m.sector_name} · ` : ''}{m.name}</span>
                <span style={{ color: 'var(--ink-800)' }}>{m.capacity}</span>
                <span style={{ color: '#fff', fontWeight: 600 }}>{money(m.price)}</span>
              </div>
            ))}
          </div>
        </>
      )}
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>Los lotes con 🚌 incluyen traslado.</div>
    </div>
  );
}

function PorRRPP({ links, mesas = [], pct }) {
  // group by rrpp: links (max_uses × precio) + mesas que generó (precio de la mesa)
  const byRrpp = {};
  links.forEach(l => {
    const k = l.rrpp_id;
    byRrpp[k] = byRrpp[k] || { name: l.rrpp_name || '—', vendidas: 0, mesas: 0, monto: 0 };
    byRrpp[k].vendidas += (l.max_uses || 0);
    byRrpp[k].monto += linkValue(l);
  });
  mesas.forEach(m => {
    if (!m.generated_by) return;
    const k = m.generated_by;
    byRrpp[k] = byRrpp[k] || { name: m.generated_by_name || '—', vendidas: 0, mesas: 0, monto: 0 };
    byRrpp[k].mesas += 1;
    byRrpp[k].monto += Number(m.price || 0);
  });
  const rows = Object.values(byRrpp).sort((a, b) => b.monto - a.monto);

  return (
    <div className="lb-fade" style={{ marginTop: 22 }}>
      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.6fr 0.7fr 0.7fr 1fr 1fr' }}>
          <span>Vendedor</span><span>Entradas</span><span>Mesas</span><span>Monto</span><span>A rendir</span>
        </div>
        {rows.map((r, i) => (
          <div key={i} className="lb-row" style={{ gridTemplateColumns: '1.6fr 0.7fr 0.7fr 1fr 1fr' }}>
            <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', color: '#fff' }}>{r.name}</span>
            <span style={{ color: 'var(--ink-800)' }}>{r.vendidas}</span>
            <span style={{ color: 'var(--ink-800)' }}>{r.mesas || '—'}</span>
            <span style={{ color: '#fff', fontWeight: 600 }}>{money(r.monto)}</span>
            <span style={{ color: 'var(--red-400)', fontWeight: 600 }}>{money(Math.round(r.monto * (1 - pct / 100)))}</span>
          </div>
        ))}
        {rows.length === 0 && <div style={{ padding: '20px 0', fontSize: 13, color: 'var(--ink-600)' }}>Sin ventas de RRPP en este evento.</div>}
      </div>
      <div style={{ marginTop: 10, fontSize: 11, color: 'var(--ink-600)' }}>"A rendir" = monto vendido (entradas + mesas) − {pct}% de comisión del vendedor.</div>
    </div>
  );
}

function LinksGenerados({ mode = 'rrpp', event, links, tickets, mesas = [], api, reload, orgLogo }) {
  const { user } = useAuth();
  const canManage = ['owner', 'admin'].includes(user?.role); // borrar / reembolsar: solo dueño y admin
  const [fLote, setFLote] = useState('');
  const [fVend, setFVend] = useState('');
  const [fAsist, setFAsist] = useState('');
  const [openId, setOpenId] = useState(null);
  const [qrTicket, setQrTicket] = useState(null); // modal Ver QR
  const [pag, setPag] = useState(1);
  const isOnline = mode === 'online';

  // Borrar un QR (RRPP) — el backend revierte sold_count, used_count y puntos.
  async function delTicket(t) {
    if (!window.confirm(`¿Borrar el QR de ${fullName(t.buyer_name, t.buyer_lastname) || 'este asistente'}? Libera el lugar.`)) return;
    try { await api.del(`/api/tickets/${t.id}`); reload(); } catch (e) { alert('No se pudo borrar: ' + e.message); }
  }
  // Reembolsar una compra online (toda la compra del mismo pago).
  async function refundTicket(t) {
    if (!window.confirm('Se le devuelve la plata al comprador, ¿confirmás?')) return;
    try { const r = await api.post(`/api/tickets/${t.id}/refund`, {}); reload(); alert(`Reembolso realizado (${r.reembolsados || 1} QR cancelado/s).`); }
    catch (e) { alert('No se pudo reembolsar: ' + e.message); }
  }
  const refundable = t => t.channel === 'online' && t.payment_id && t.status !== 'usado' && t.status !== 'cancelado';

  // Mesas GENERADAS POR UN RRPP: son como un link generado por el RRPP → se listan
  // también acá (además de en "Venta por RRPP"). Las de venta online o directa del dueño no.
  const rrppMesas = (mesas || []).filter(m => m.status === 'sold' && m.generated_by && m.generated_by_role === 'rrpp');
  // El dropdown de vendedores suma los RRPP que generaron mesas aunque no tengan links.
  const vendors = [...new Map([
    ...links.map(l => [l.rrpp_id, l.rrpp_name]),
    ...rrppMesas.map(m => [m.generated_by, m.generated_by_name]),
  ]).entries()];
  const lots = event.lots || [];
  // Mesas a mostrar en "Links por RRPP" (respeta el filtro de vendedor; el de lote las oculta).
  let shownMesas = isOnline ? [] : rrppMesas;
  if (fVend) shownMesas = shownMesas.filter(m => m.generated_by === fVend);
  if (fLote) shownMesas = [];

  let filtered = links;
  if (fLote) filtered = filtered.filter(l => l.lot_id === fLote);
  if (fVend) filtered = filtered.filter(l => l.rrpp_id === fVend);
  if (fAsist.trim()) {
    const q = fAsist.trim().toLowerCase();
    const linkIds = new Set(tickets.filter(t => `${t.buyer_name || ''} ${t.buyer_lastname || ''}`.toLowerCase().includes(q)).map(t => t.link_id));
    // También busca por la NOTA y por el código del link: muchas veces no se busca por
    // el nombre de quien va, sino por cómo se identificó el link al generarlo.
    filtered = filtered.filter(l => linkIds.has(l.id)
      || (l.note || '').toLowerCase().includes(q)
      || (l.code || '').toLowerCase().includes(q));
  }

  async function invalidate(id) {
    if (!window.confirm('¿Invalidar este link?\n\nSus QRs se borran y dejan de servir en la puerta. Los que ya ingresaron no se tocan.')) return;
    try {
      const r = await api.del(`/api/rrpp-links/${id}`);
      // Se informa qué pasó de verdad: si alguno ya había ingresado, quien borra tiene
      // que enterarse — significa que esa persona está adentro igual.
      const b = Number(r?.qr_borrados) || 0, u = Number(r?.qr_ya_usados) || 0;
      if (u > 0) alert(`Se borraron ${b} ${b === 1 ? 'QR' : 'QRs'}.\n\nOJO: ${u} ${u === 1 ? 'ya había ingresado' : 'ya habían ingresado'} y no se ${u === 1 ? 'tocó' : 'tocaron'} — esa gente ya está adentro.`);
      reload();
    } catch (e) { alert(e.message); }
  }

  // ── Venta online (compra directa, sin link RRPP) ──
  const lotPrice = {}, lotName = {};
  (event.lots || []).forEach(l => { lotPrice[l.id] = Number(l.price) || 0; lotName[l.id] = l.name; });
  // Valor real de una entrada: el neto que se pagó (price_paid, ya con el descuento
  // del cupón). Los tickets viejos sin ese dato caen al precio de lista del lote.
  const ticketVal = (t) => (t.price_paid != null ? Number(t.price_paid) : (lotPrice[t.lot_id] || 0));
  let onlineTickets = tickets.filter(t => t.channel === 'online' && (t.status === 'pagado' || t.status === 'usado'));
  if (fLote) onlineTickets = onlineTickets.filter(t => t.lot_id === fLote);
  if (fAsist.trim()) {
    const q = fAsist.trim().toLowerCase();
    onlineTickets = onlineTickets.filter(t => `${t.buyer_name || ''} ${t.buyer_lastname || ''}`.toLowerCase().includes(q));
  }
  const onlineTotal = onlineTickets.reduce((a, t) => a + ticketVal(t), 0);

  // Paginado de a 100. Cada pestaña muestra UNA lista (online o links), así que alcanza
  // con un solo contador de página.
  //
  // Los totales de arriba (cantidad y plata) se siguen calculando sobre la lista COMPLETA
  // filtrada, no sobre la página: si mostraran solo lo visible, el dueño leería "$300.000"
  // cuando vendió el triple.
  const listaCompleta = isOnline ? onlineTickets : filtered;
  const paginas = Math.max(1, Math.ceil(listaCompleta.length / POR_PAGINA));
  const pagActual = Math.min(pag, paginas);   // si un filtro achica la lista, no quedarse en una página que ya no existe
  const desde = (pagActual - 1) * POR_PAGINA;
  const onlineVisibles = isOnline ? onlineTickets.slice(desde, desde + POR_PAGINA) : [];
  const linksVisibles = isOnline ? [] : filtered.slice(desde, desde + POR_PAGINA);

  return (
    <div className="lb-fade" style={{ marginTop: 22 }}>
      {/* filtros */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 16, marginBottom: 14, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
          <select className="lb-select" style={{ padding: '9px 32px 9px 12px', fontSize: 13, minWidth: 130 }} value={fLote} onChange={e => { setFLote(e.target.value); setPag(1); }}>
            <option value="">Todos los lotes</option>
            {lots.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </select>
          {!isOnline && (
            <select className="lb-select" style={{ padding: '9px 32px 9px 12px', fontSize: 13, minWidth: 150 }} value={fVend} onChange={e => { setFVend(e.target.value); setPag(1); }}>
              <option value="">Todos los vendedores</option>
              {vendors.map(([id, name]) => <option key={id} value={id}>{name}</option>)}
            </select>
          )}
          <input className="lb-input" style={{ padding: '9px 12px', fontSize: 13, width: 180 }} placeholder="Buscar asistente, nota o código…" value={fAsist} onChange={e => { setFAsist(e.target.value); setPag(1); }} />
          {(fLote || fVend || fAsist) && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => { setFLote(''); setFVend(''); setFAsist(''); setPag(1); }}>Limpiar</button>}
        </div>
      </div>

      {/* ── ONLINE (compra directa) ── */}
      {isOnline && (
        <div style={{ marginBottom: 22 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
            <span className="lb-status lb-status--paid">Online</span>
            <span className="lb-eyebrow">Compra directa · {onlineTickets.length} entradas · {money(onlineTotal)}</span>
          </div>
          <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
            <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr 0.9fr auto' }}>
              <span>Comprador</span><span>Lote</span><span>Código</span><span>Fecha/hora</span><span>Valor</span><span style={{ textAlign: 'right' }}>Acciones</span>
            </div>
            {onlineVisibles.map(t => (
              <div key={t.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 1fr 0.9fr auto', alignItems: 'center' }}>
                <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{fullName(t.buyer_name, t.buyer_lastname)}</span>
                <span style={{ color: 'var(--ink-700)', fontSize: 13 }}>{lotName[t.lot_id] || '—'}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-700)' }}>{t.qr_code}</span>
                <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>{fmtDateTime(t.created_at)}</span>
                <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{money(ticketVal(t))}</span>
                <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                  <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setQrTicket(t)}>Ver QR</button>
                  {canManage && refundable(t) && <button className="lb-btn lb-btn--ghost lb-btn--sm" style={{ color: 'var(--red-400)' }} onClick={() => refundTicket(t)}>Reembolsar</button>}
                </span>
              </div>
            ))}
            {onlineTickets.length === 0 && <div style={{ padding: '18px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>Sin ventas online.</div>}
          </div>
          <BarraPaginado pagActual={pagActual} paginas={paginas} desde={desde} total={onlineTickets.length} onIr={setPag} />
        </div>
      )}

      {/* ── RRPP (links) ── */}
      {!isOnline && (<>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <span className="lb-status lb-status--used">RRPP</span>
        <span className="lb-eyebrow">Links generados · {filtered.length}</span>
      </div>
      <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
        <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.1fr 1.3fr 0.9fr 0.9fr auto' }}>
          <span>Lote</span><span>Vendedor</span><span>Usos</span><span>Valor</span><span style={{ textAlign: 'right' }}>Asistentes</span>
        </div>
        {linksVisibles.map(l => {
          const left = l.max_uses - l.used_count;
          const open = openId === l.id;
          const asists = tickets.filter(t => t.link_id === l.id);
          return (
            <div key={l.id} style={{ borderBottom: '1px solid rgba(255,255,255,0.07)' }}>
              <div className="lb-row lb-linkrow" onClick={() => setOpenId(open ? null : l.id)} style={{ gridTemplateColumns: '1.1fr 1.3fr 0.9fr 0.9fr auto', borderBottom: 0, cursor: 'pointer' }}>
                {/* Debajo del lote, cuándo generó el link el RRPP: con la regla de
                    "la venta se cuenta al generar", esta es la fecha de esa venta. */}
                <div>
                  {/* La nota va ARRIBA del lote: es lo que identifica al link ("los del
                      cumple de Mica") y es por donde se lo busca. El lote se repite en
                      decenas de filas; la nota no. */}
                  {l.note && (
                    <div style={{ fontSize: 10.5, color: 'var(--red-400)', lineHeight: 1.3, marginBottom: 2, wordBreak: 'break-word' }}>
                      {l.note}
                    </div>
                  )}
                  <div style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', color: '#fff' }}>{l.lot_name}</div>
                  <div style={{ fontSize: 10, color: 'var(--ink-600)', letterSpacing: '0.04em' }}>{fmtDateTime(l.created_at)}</div>
                </div>
                <div>
                  <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{l.rrpp_name}</div>
                  <div style={{ fontSize: 10, color: 'var(--ink-600)', fontFamily: 'monospace', letterSpacing: '0.06em' }}>/{l.code}</div>
                </div>
                <span style={{ color: left === 0 ? 'var(--ink-600)' : 'var(--ink-800)' }}>{l.used_count} / {l.max_uses} <span style={{ fontSize: 11, color: 'var(--ink-600)' }}>· {left} libres</span></span>
                <span style={{ color: 'var(--ink-800)' }}>{money(linkValue(l))}</span>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'flex-end' }}>
                  {/* Reenviar el link: el original se pierde en el WhatsApp del RRPP o del
                      comprador (mensajes temporales) y generar otro contaría como otra venta. */}
                  <BotonCompartirLink link={l} event={event} compacto />
                  <span style={{ fontWeight: 600, fontSize: 11, letterSpacing: '0.1em', textTransform: 'uppercase', color: open ? '#fff' : 'var(--ink-700)' }}>Ver asistentes {open ? '▾' : '▸'}</span>
                  <span onClick={e => e.stopPropagation()}><button className="lb-iconbtn" onClick={() => invalidate(l.id)} title="Invalidar link">✕</button></span>
                </div>
              </div>
              {open && (
                <div style={{ padding: '6px 0 18px' }}>
                  <div className="lb-eyebrow" style={{ padding: '4px 0 10px', color: 'var(--ink-600)' }}>Asistentes · {asists.length}</div>
                  {asists.length === 0 && <div style={{ fontSize: 12, color: 'var(--ink-600)', padding: '8px 0' }}>Sin QRs generados desde este link todavía.</div>}
                  {asists.map(t => (
                    <div key={t.id} className="lb-row" style={{ gridTemplateColumns: '1.4fr 1fr 1fr 0.9fr auto', padding: '11px 0', borderBottom: '1px solid rgba(255,255,255,0.05)', alignItems: 'center' }}>
                      <div style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{fullName(t.buyer_name, t.buyer_lastname)}</div>
                      <span style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--ink-700)' }}>{t.qr_code}</span>
                      <span style={{ color: 'var(--ink-700)', fontSize: 12 }}>{fmtDateTime(t.created_at)}</span>
                      <span><StatusChip status={t.status} /></span>
                      <span style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                        <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => setQrTicket(t)}>Ver QR</button>
                        {canManage && <button className="lb-btn lb-btn--ghost lb-btn--sm" onClick={() => delTicket(t)}>Borrar</button>}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
        {filtered.length === 0 && <div style={{ padding: '24px 0', textAlign: 'center', fontSize: 13, color: 'var(--ink-600)' }}>No hay links con esos filtros.</div>}
      </div>
      <BarraPaginado pagActual={pagActual} paginas={paginas} desde={desde} total={filtered.length} onIr={setPag} />

      {/* Mesas generadas por un RRPP (mismo criterio que un link generado por el RRPP). */}
      {shownMesas.length > 0 && (<>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '24px 0 10px' }}>
          <span className="lb-status lb-status--used">RRPP</span>
          <span className="lb-eyebrow">Mesas generadas · {shownMesas.length}</span>
        </div>
        <div className="lb-card tk-scroll-x" style={{ padding: '6px 24px' }}>
          <div className="lb-row lb-th" style={{ gridTemplateColumns: '1.1fr 1.3fr 0.9fr 0.9fr auto' }}>
            <span>Mesa</span><span>Vendedor</span><span>Personas</span><span>Valor</span><span style={{ textAlign: 'right' }}>Gestión</span>
          </div>
          {shownMesas.map(m => (
            <div key={m.id} className="lb-row" style={{ gridTemplateColumns: '1.1fr 1.3fr 0.9fr 0.9fr auto', alignItems: 'center' }}>
              <span style={{ fontFamily: 'var(--font-display)', fontWeight: 600, fontSize: 14, textTransform: 'uppercase', color: '#fff' }}>{m.sector_name ? `${m.sector_name} · ` : ''}{m.name}</span>
              <span style={{ color: '#fff', fontSize: 13, fontWeight: 600 }}>{m.generated_by_name || '—'}</span>
              <span style={{ color: 'var(--ink-800)' }}>{m.capacity}</span>
              <span style={{ color: 'var(--ink-800)' }}>{money(m.price)}</span>
              <span style={{ textAlign: 'right', fontSize: 11, color: 'var(--ink-600)' }}>Pestaña Mesas</span>
            </div>
          ))}
        </div>
      </>)}
      </>)}

      {/* Ver QR individual (lectura / reenvío) */}
      {qrTicket && (
        <div className="lb-modal-backdrop" onClick={() => setQrTicket(null)}>
          <div className="lb-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 360, padding: 24, textAlign: 'center' }}>
            <div className="lb-h" style={{ fontSize: 18, marginBottom: 4 }}>{event.name}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-600)', marginBottom: 16 }}>{fullName(qrTicket.buyer_name, qrTicket.buyer_lastname)} · {lotName[qrTicket.lot_id] || '—'}</div>
            <TicketQR value={qrTicket.qr_code} transportValue={qrTicket.transport_qr || null} logo={orgLogo} eventName={event.name} date={event.date} lotName={lotName[qrTicket.lot_id]} attendeeName={fullName(qrTicket.buyer_name, qrTicket.buyer_lastname)} filename={`${event.name}-${qrTicket.qr_code}.png`} brightnessHint={false} />
            {qrTicket.status === 'cancelado' && <div className="lb-status lb-status--used" style={{ marginTop: 12 }}>Reembolsada / cancelada</div>}
          </div>
        </div>
      )}
    </div>
  );
}

function StatusChip({ status }) {
  const map = { generado: 'gen', pagado: 'paid', usado: 'used' };
  return <span className={'lb-status lb-status--dot lb-status--' + (map[status] || 'gen')}>{status}</span>;
}
